import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { GetHelpWidget } from './dashboard/widgets/GetHelpWidget';

/**
 * Get Help modal (PD-15235) — opened from the header's help icon when the
 * portal site's headerSupportDisplay is 'help-icon'. Same shell treatment
 * as SupportRequestModal (bottom sheet on mobile, centered dialog on md+),
 * wrapping the same Get Help card the Support and Account pages render.
 */
export function GetHelpModal({
	isOpen,
	onClose,
}: {
	isOpen: boolean;
	onClose: () => void;
}): JSX.Element | null {
	const { t } = useTranslation();

	// Close on Escape while open — mirrors SupportRequestModal.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const onKey = (e: KeyboardEvent): void => {
			if (e.key === 'Escape') {
				onClose();
			}
		};
		document.addEventListener('keydown', onKey);
		return (): void => {
			document.removeEventListener('keydown', onKey);
		};
	}, [isOpen, onClose]);

	if (!isOpen) {
		return null;
	}

	return (
		<>
			<button
				type="button"
				aria-label={t('Close')}
				onClick={onClose}
				className="fixed inset-0 z-40 bg-black/40 drawer-backdrop-in"
			/>
			<div
				className="fixed inset-x-0 bottom-[max(calc(36px_+_env(safe-area-inset-bottom)),48px)] z-40 rounded-t-2xl bg-brand-surface shadow-2xl flex flex-col overflow-hidden filter-tray-in max-h-[80vh] md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md md:rounded-2xl md:border md:border-brand-border"
				role="dialog"
				aria-modal="true"
				aria-label={t('Get Help')}
			>
				<div className="flex items-center justify-between px-4 h-12 border-b border-brand-border shrink-0">
					<h2 className="text-base font-semibold text-default">
						{t('Get Help')}
					</h2>
					<button
						type="button"
						onClick={onClose}
						aria-label={t('Close')}
						className="p-2 -mr-2 text-brand-muted hover:text-default min-h-touch"
					>
						<FontAwesomeIcon icon={faTimes} />
					</button>
				</div>
				<div className="overflow-y-auto p-4">
					{/* The dialog header carries the title, so the content renders
					    plain — a second framed card in here read as odd spacing
					    (owner feedback). */}
					<GetHelpWidget plain />
				</div>
			</div>
		</>
	);
}
